export type FullPageExportFormat = 'pdf' | 'png';

/**
 * Explicit delivery for full-page export.
 * - `download` — local `<a download>` only (never opens the OS share sheet)
 * - `share` — Web Share API only (no silent download fallback)
 */
export type FullPageExportDelivery = 'download' | 'share';

/** Pipeline stage for attributable export failures (orchestration + encode/share helpers). */
export type FullPageExportStage =
  | 'resolve-surface'
  | 'capture'
  | 'encode-pdf'
  | 'encode-png'
  | 'share-download';

export interface FullPageExportRequest {
  readonly element: HTMLElement;
  readonly filenameBase: string;
  readonly format: FullPageExportFormat;
  readonly delivery: FullPageExportDelivery;
}

export interface ShareOrDownloadOptions {
  readonly filename: string;
  readonly mimeType: string;
}

export interface BuildFullPageExportFilenameInput {
  readonly tenantCode: string;
  readonly screenSlug: string;
  readonly now?: Date;
}

/** A4 portrait (ISO 216) — shared PDF page size and PNG strip aspect. */
export const A4_PORTRAIT_WIDTH_MM = 210;
export const A4_PORTRAIT_HEIGHT_MM = 297;
export const A4_PORTRAIT_ASPECT_RATIO = A4_PORTRAIT_WIDTH_MM / A4_PORTRAIT_HEIGHT_MM;

/**
 * Thrown when `#main` has no `[data-admin-export-surface="page"]` or
 * `[data-testid="admin-shell-grid"]`. UI maps `i18nKey` → `errorMissingSurface`.
 */
export class FullPageExportSurfaceMissingError extends Error {
  readonly i18nKey = 'errorMissingSurface' as const;

  constructor() {
    super('errorMissingSurface');
    this.name = 'FullPageExportSurfaceMissingError';
  }
}

/**
 * Stage-tagged export failure for upstream logging / toast mapping.
 * Prefer throwing this from capture/encode/share helpers; orchestration
 * wraps unknown errors with the active stage and preserves `cause`.
 */
export class FullPageExportStageError extends Error {
  readonly stage: FullPageExportStage;
  readonly cause?: unknown;

  constructor(
    stage: FullPageExportStage,
    message: string,
    options?: { readonly cause?: unknown },
  ) {
    super(message);
    this.name = 'FullPageExportStageError';
    this.stage = stage;
    if (options?.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}

/**
 * User cancelled the Web Share sheet (`AbortError`). Not delivery success and not a
 * controlled export failure — UI must not toast success or error (AC-07 / Fix 7 G13).
 * Stable identity: `name === 'FullPageExportShareAbortedError'` and `aborted === true`.
 */
export class FullPageExportShareAbortedError extends FullPageExportStageError {
  /** Stable cancel flag for menu catch (and duck-typed callers). */
  readonly aborted = true as const;

  constructor(cause?: unknown) {
    super(
      'share-download',
      'Web Share was cancelled',
      cause !== undefined ? { cause } : undefined,
    );
    this.name = 'FullPageExportShareAbortedError';
  }
}
